import { ChevronDown, ChevronUp, Save } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTeamTab } from '../../TeamTabContext';
import type { TeamMember } from '../../TeamTabContext';

export const DistributionCriteriaPanel: React.FC = () => {
  const ctx = useTeamTab();
  const {
    distributionCriteriaSectionRef,
    highlightDistributionCriteriaSection,
    teamMembers,
    setTeamMembers,
    originalTeamMembers,
    handleUpdateTeamMember,
    reloadTeamMembers,
    criteriaOptions,
    openCriteriaDropdown,
    setOpenCriteriaDropdown,
    criteriaSearchQueries,
    setCriteriaSearchQueries,
    criteriaDropdownRef,
    savingDistributionCriteria,
    setSavingDistributionCriteria,
    distributionCriteriaSaved,
    setDistributionCriteriaSaved,
    distributionCriteriaSaveError,
    setDistributionCriteriaSaveError,
  } = ctx;

  const [includingIds, setIncludingIds] = useState<Set<number>>(new Set());

  const hiddenMembers = teamMembers.filter(
    (m) =>
      m.isActive !== false &&
      !(m.includeInCapacity !== false || m.includeInHistoricalCapacity === true)
  );

  const handleIncludeMember = async (member: TeamMember) => {
    const id = member.id;
    if (id == null) return;
    setIncludingIds((prev) => new Set(prev).add(id));
    try {
      await handleUpdateTeamMember({ ...member, includeInCapacity: true });
      await reloadTeamMembers();
    } catch (e) {
      setDistributionCriteriaSaveError(
        e instanceof Error ? e.message : 'Failed to include member.'
      );
    } finally {
      setIncludingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const hiddenMembersBlock =
    hiddenMembers.length > 0 ? (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: '12px 14px',
          backgroundColor: 'var(--profile-nested-bg)',
          border: '1px solid var(--profile-nested-border)',
          borderRadius: '8px',
        }}
      >
        <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
          {hiddenMembers.length} member(s) not included in distribution
        </span>
        {hiddenMembers.map((m) => (
          <div
            key={m.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '6px 10px',
              backgroundColor: 'var(--profile-nested-table-row-bg)',
              border: '1px solid var(--profile-nested-border)',
              borderRadius: '6px',
            }}
          >
            <span style={{ fontSize: '13px', color: 'var(--color-text)' }}>{m.name}</span>
            <button
              type="button"
              disabled={m.id == null || includingIds.has(m.id)}
              onClick={() => void handleIncludeMember(m)}
              style={{
                padding: '6px 12px',
                backgroundColor: m.id == null || includingIds.has(m.id) ? '#9ca3af' : '#6366f1',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: m.id == null || includingIds.has(m.id) ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {m.id != null && includingIds.has(m.id) ? 'Adding...' : 'Include'}
            </button>
          </div>
        ))}
      </div>
    ) : null;

  return (
    <section
      id="team-zone-distribution"
      className="settings-zone"
      aria-labelledby="team-zone-distribution-h2"
      style={{ marginTop: '24px' }}
    >
      <div className="settings-zone__header">
        <h2 id="team-zone-distribution-h2" className="settings-zone__title">
          Forecast distribution
        </h2>
      </div>
      <div
        ref={distributionCriteriaSectionRef as React.Ref<HTMLDivElement>}
        id="profile-distribution-criteria"
        className={
          highlightDistributionCriteriaSection
            ? 'profile-distribution-criteria-highlight'
            : undefined
        }
        style={{
          padding: '20px',
          backgroundColor: 'var(--profile-nested-bg)',
          borderRadius: '12px',
          border: '1px solid var(--profile-nested-border)',
        }}
      >
        <h3
          style={{
            marginBottom: '8px',
            fontSize: '15px',
            fontWeight: '600',
            color: 'var(--color-text)',
          }}
        >
          Distribution criteria (for Forecast)
        </h3>
        <p
          style={{
            marginBottom: '16px',
            fontSize: '12px',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.5,
          }}
        >
          This block works together with <strong>Forecast → Distribute to release</strong>: when you
          run <strong>Suggest distribution</strong>, tasks from the Forecast table are matched to
          people using these rules (component, label, priority, plus any custom field with{' '}
          <strong>In distribution</strong> turned on under{' '}
          <strong>Settings → Project → Forecast custom fields</strong>). It does{' '}
          <strong>not</strong> replace the role column above: set <strong>Developer</strong> vs{' '}
          <strong>QA</strong> there for capacity, Dev/QA split, and &quot;Suggest from history&quot;
          from worklogs. Only members with <strong>Capacity</strong> or <strong>Historical</strong>{' '}
          checked appear here. Leave all fields empty for a member to allow any task for them.
        </p>
        {(() => {
          // Diff against the original snapshot — same pattern as Team Roster
          // "Save Capacity Settings". JSON.stringify is fine here (small list,
          // criteria payload is plain arrays).
          const distributionCriteriaChanged = teamMembers.some((member) => {
            const original = originalTeamMembers.find((om) => om.id === member.id);
            if (!original) return false;
            return (
              JSON.stringify(member.distributionCriteria ?? {}) !==
              JSON.stringify(original.distributionCriteria ?? {})
            );
          });
          if (
            !distributionCriteriaChanged &&
            !distributionCriteriaSaved &&
            !distributionCriteriaSaveError
          ) {
            return null;
          }
          return (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              {distributionCriteriaChanged && (
                <button
                  type="button"
                  onClick={async () => {
                    setSavingDistributionCriteria(true);
                    setDistributionCriteriaSaved(false);
                    setDistributionCriteriaSaveError(null);
                    try {
                      const changed = teamMembers.filter((m) => {
                        const original = originalTeamMembers.find((om) => om.id === m.id);
                        if (!original) return false;
                        return (
                          JSON.stringify(m.distributionCriteria ?? {}) !==
                          JSON.stringify(original.distributionCriteria ?? {})
                        );
                      });
                      await Promise.all(changed.map((m) => handleUpdateTeamMember(m)));
                      await reloadTeamMembers();
                      setDistributionCriteriaSaved(true);
                      setTimeout(() => setDistributionCriteriaSaved(false), 5000);
                    } catch (e) {
                      console.error('Error saving distribution criteria:', e);
                      setDistributionCriteriaSaveError(
                        e instanceof Error ? e.message : 'Failed to save distribution criteria.'
                      );
                    } finally {
                      setSavingDistributionCriteria(false);
                    }
                  }}
                  disabled={savingDistributionCriteria}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: savingDistributionCriteria ? '#9ca3af' : '#6366f1',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: savingDistributionCriteria ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Save size={16} />
                  {savingDistributionCriteria ? 'Saving...' : 'Save distribution criteria'}
                </button>
              )}
              {distributionCriteriaSaved && (
                <div className="success-message" role="status">
                  <span className="success-icon">✓</span>
                  Distribution criteria saved successfully!
                </div>
              )}
              {distributionCriteriaSaveError && (
                <div className="error-message" role="alert" style={{ color: 'var(--color-error)' }}>
                  {distributionCriteriaSaveError}
                </div>
              )}
            </div>
          );
        })()}
        <div
          ref={criteriaDropdownRef as React.Ref<HTMLDivElement>}
          style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
        >
          {(() => {
            const membersWithCapacityOrHistorical = teamMembers.filter(
              (m) =>
                m.isActive !== false &&
                (m.includeInCapacity === true ||
                  m.includeInCapacity === undefined ||
                  m.includeInHistoricalCapacity === true)
            );
            if (membersWithCapacityOrHistorical.length === 0) {
              return (
                <>
                  <div
                    style={{
                      padding: '16px',
                      fontSize: '13px',
                      color: 'var(--color-text-secondary)',
                      backgroundColor: 'var(--profile-nested-table-row-muted)',
                      borderRadius: '8px',
                      border: '1px solid var(--profile-nested-border)',
                    }}
                  >
                    No team members with Capacity or Historical checked. Enable at least one of
                    these for members above to configure distribution criteria.
                  </div>
                  <Link
                    to="/settings?tab=team&section=team-add-member"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      alignSelf: 'flex-start',
                      padding: '8px 16px',
                      backgroundColor: '#6366f1',
                      color: '#fff',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      textDecoration: 'none',
                    }}
                  >
                    Add team member
                  </Link>
                  {hiddenMembersBlock}
                </>
              );
            }
            return (
              <>
                {membersWithCapacityOrHistorical.map((member) => {
                  const cr = member.distributionCriteria ?? {
                    component: [],
                    label: [],
                    priority: [],
                    customFields: {},
                  };
                  // Built-in criteria: shipped with the product, always visible.
                  // Cluster used to live here as a hardcoded entry — it has been
                  // demoted to a Forecast custom field. Register it under
                  // Settings → Project → Forecast custom fields and toggle
                  // "In distribution" on if you still want per-member cluster
                  // routing.
                  type CriteriaEntry =
                    | {
                        kind: 'builtin';
                        key: 'component' | 'label' | 'priority';
                        label: string;
                        options: string[];
                      }
                    | {
                        kind: 'custom';
                        orgFieldId: number;
                        label: string;
                        options: string[];
                      };
                  const criteriaTypes: CriteriaEntry[] = [
                    {
                      kind: 'builtin',
                      key: 'component',
                      label: 'Component',
                      options: criteriaOptions.components,
                    },
                    {
                      kind: 'builtin',
                      key: 'label',
                      label: 'Label',
                      options: ['__empty__', ...criteriaOptions.labels],
                    },
                    {
                      kind: 'builtin',
                      key: 'priority',
                      label: 'Priority',
                      options: criteriaOptions.priorities,
                    },
                    ...criteriaOptions.customFields.map<CriteriaEntry>((cf) => ({
                      kind: 'custom',
                      orgFieldId: cf.orgFieldId,
                      label: cf.displayName,
                      options: ['__empty__', ...cf.distinctValues],
                    })),
                  ];
                  const writeCriteria = (entry: CriteriaEntry, next: string[]) => {
                    let updatedCriteria: NonNullable<TeamMember['distributionCriteria']>;
                    if (entry.kind === 'builtin') {
                      updatedCriteria = {
                        ...cr,
                        [entry.key]: next,
                      };
                    } else {
                      updatedCriteria = {
                        ...cr,
                        customFields: {
                          ...(cr.customFields ?? {}),
                          [String(entry.orgFieldId)]: next,
                        },
                      };
                    }
                    const updated = {
                      ...member,
                      distributionCriteria: updatedCriteria,
                    };
                    setTeamMembers((prev) => prev.map((m) => (m.id === member.id ? updated : m)));
                  };
                  return (
                    <div
                      key={member.id}
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '12px 14px',
                        backgroundColor: 'var(--profile-nested-table-row-bg)',
                        border: '1px solid var(--profile-nested-border)',
                        borderRadius: '8px',
                      }}
                    >
                      <span
                        style={{
                          minWidth: '140px',
                          fontWeight: 600,
                          fontSize: '14px',
                          color: 'var(--color-text)',
                          paddingTop: '6px',
                        }}
                      >
                        {member.name}
                      </span>
                      {criteriaTypes.map((entry) => {
                        const entryKey =
                          entry.kind === 'builtin' ? entry.key : `cf-${entry.orgFieldId}`;
                        const typeLabel = entry.label;
                        const options = entry.options;
                        const dropdownId = `${member.id}-${entryKey}`;
                        const isOpen = openCriteriaDropdown === dropdownId;
                        const selected =
                          entry.kind === 'builtin'
                            ? cr[entry.key] || []
                            : (cr.customFields?.[String(entry.orgFieldId)] ?? []);
                        const searchQuery = criteriaSearchQueries[dropdownId] ?? '';
                        const filteredOptions = !searchQuery.trim()
                          ? options
                          : options.filter((o) =>
                              String(o).toLowerCase().includes(searchQuery.trim().toLowerCase())
                            );
                        const allSelected =
                          filteredOptions.length > 0 &&
                          filteredOptions.every((opt) => selected.includes(opt));
                        const someSelected =
                          filteredOptions.some((opt) => selected.includes(opt)) && !allSelected;
                        return (
                          <div key={entryKey} style={{ position: 'relative' }}>
                            <button
                              type="button"
                              onClick={() => setOpenCriteriaDropdown(isOpen ? null : dropdownId)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                fontSize: '13px',
                                border: '1px solid var(--color-border-strong)',
                                borderRadius: '6px',
                                backgroundColor: 'var(--profile-nested-table-row-bg)',
                                color: 'var(--color-text)',
                                cursor: 'pointer',
                                minWidth: '140px',
                                justifyContent: 'space-between',
                              }}
                            >
                              <span>
                                {typeLabel}:{' '}
                                {selected.length === 0
                                  ? 'All'
                                  : selected.length === 1
                                    ? selected[0] === '__empty__'
                                      ? '(empty)'
                                      : selected[0]
                                    : `${selected.length} selected`}
                              </span>
                              {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                            {isOpen && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  marginTop: '4px',
                                  zIndex: 50,
                                  minWidth: '220px',
                                  maxWidth: '320px',
                                  maxHeight: '280px',
                                  backgroundColor: 'var(--profile-nested-table-row-bg)',
                                  border: '1px solid var(--profile-nested-border)',
                                  borderRadius: '8px',
                                  boxShadow: 'var(--shadow-md)',
                                  overflow: 'hidden',
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div
                                  style={{
                                    padding: '8px',
                                    borderBottom: '1px solid var(--profile-nested-border)',
                                  }}
                                >
                                  <input
                                    type="text"
                                    placeholder="Type to search..."
                                    value={searchQuery}
                                    onChange={(e) =>
                                      setCriteriaSearchQueries((prev) => ({
                                        ...prev,
                                        [dropdownId]: e.target.value,
                                      }))
                                    }
                                    style={{
                                      width: '100%',
                                      padding: '6px 8px',
                                      fontSize: '13px',
                                      border: '1px solid var(--color-border-strong)',
                                      borderRadius: '4px',
                                      backgroundColor: 'var(--color-bg-input)',
                                      color: 'var(--color-text)',
                                    }}
                                  />
                                </div>
                                <div
                                  style={{
                                    padding: '6px 10px',
                                    borderBottom: '1px solid var(--profile-nested-border)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    backgroundColor: 'var(--profile-nested-table-row-muted)',
                                    color: 'var(--color-text)',
                                  }}
                                  onClick={() => {
                                    const next = allSelected
                                      ? selected.filter((s) => !filteredOptions.includes(s))
                                      : Array.from(
                                          new Set([...selected, ...filteredOptions])
                                        ).sort();
                                    writeCriteria(entry, next);
                                    // Defer persistence — pressing the
                                    // "Save distribution criteria" button below
                                    // commits the diff to the server.
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={allSelected}
                                    ref={(el) => {
                                      if (el) el.indeterminate = someSelected;
                                    }}
                                    readOnly
                                    style={{
                                      width: '14px',
                                      height: '14px',
                                      cursor: 'pointer',
                                    }}
                                  />
                                  {allSelected ? 'Deselect All' : 'Select All'}
                                </div>
                                <div
                                  style={{
                                    overflowY: 'auto',
                                    maxHeight: '200px',
                                    padding: '4px 0',
                                  }}
                                >
                                  {filteredOptions.length === 0 ? (
                                    <div
                                      style={{
                                        padding: '12px',
                                        fontSize: '12px',
                                        color: 'var(--color-text-secondary)',
                                      }}
                                    >
                                      No matches
                                    </div>
                                  ) : (
                                    filteredOptions.map((opt) => (
                                      <label
                                        key={opt}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                          padding: '6px 12px',
                                          cursor: 'pointer',
                                          fontSize: '13px',
                                          color: 'var(--color-text)',
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selected.includes(opt)}
                                          onChange={() => {
                                            const next = selected.includes(opt)
                                              ? selected.filter((v) => v !== opt)
                                              : [...selected, opt].sort();
                                            writeCriteria(entry, next);
                                            // Defer persistence to "Save
                                            // distribution criteria" button below.
                                          }}
                                          style={{
                                            width: '14px',
                                            height: '14px',
                                            cursor: 'pointer',
                                          }}
                                        />
                                        {opt === '__empty__' ? '(empty)' : opt}
                                      </label>
                                    ))
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {hiddenMembersBlock}
              </>
            );
          })()}
        </div>
      </div>
    </section>
  );
};
